import { AgentPanel, conversationKeys } from "../../../agent";
import { refusalText } from "../../../api/errors";
import { useApiQuery } from "../../../api/hooks";
import { useCurrentMe } from "../../../app/auth";
import { Button } from "../../../ui/Button";
import { Card } from "../../../ui/Card";
import { EmptyState, Loading } from "../../../ui/EmptyState";
import { MemoryPanel } from "../profile/MemoryPanel";
import { useMemory } from "../profile/useMemory";
import { ModuleList } from "./ModuleList";
import { PracticePanel } from "./PracticePanel";
import { adminModules, useModuleActions } from "./useModuleActions";

function Intro({ children }: { children: string }) {
  return (
    <header className="ra-page__head">
      <h1>Getting started</h1>
      <p className="muted">{children}</p>
    </header>
  );
}

/** Getting started for a member of a discipline: the tutor, the modules of their path, the practice space and the memory panel. */
function MemberOnboarding({ membershipId }: { membershipId: string }) {
  const me = useCurrentMe();
  const query = useApiQuery("onboarding", {});
  const memory = useMemory();
  const key = conversationKeys.tutor(membershipId);
  const actions = useModuleActions({ owner: "member", conversationKey: key });

  return (
    <div className="ra-page stack">
      <Intro>
        A short, optional tour in small modules, with practice in a space of your own. You can skip it, stop at any point and come back from here or from your profile. Nothing is graded.
      </Intro>
      <div className="ra-onboarding">
        <div className="ra-onboarding__main stack">
          {query.isPending && <Loading>Reading your modules</Loading>}
          {query.isError && (
            <Card role="alert">
              <p className="form-error">{refusalText(query.error)}</p>
              <Button onClick={() => void query.refetch()}>Try again</Button>
            </Card>
          )}
          {query.data && (
            <>
              <ModuleList modules={query.data.modules} actions={actions} agentAvailable={me.agent.available} />
              <PracticePanel practice={query.data.practice} modules={query.data.modules} />
            </>
          )}
          {memory.memory && <MemoryPanel memory={memory.memory} editable={false} compact onEdit={() => undefined} />}
        </div>
        <aside className="ra-onboarding__side" aria-label="Your guide">
          <AgentPanel
            conversationKey={key}
            mode="tutor"
            title="Your guide"
            emptyHint="The agent greets you here when you first sign in. You can also ask it to start a module."
          />
        </aside>
      </div>
    </div>
  );
}

/** Getting started for the admin: one module, A1, and the agent in its onboarding mode. There is no practice space. */
function AdminOnboarding({ personId }: { personId: string }) {
  const me = useCurrentMe();
  const memory = useMemory();
  const key = conversationKeys.adminOnboarding(personId);
  const actions = useModuleActions({ owner: "admin", conversationKey: key });
  const modules = adminModules(memory.memory);

  return (
    <div className="ra-page stack">
      <Intro>A short, optional module for the admin's three jobs: accounts, the model connection and the disciplines. You can skip it and come back later.</Intro>
      <div className="ra-onboarding">
        <div className="ra-onboarding__main stack">
          {memory.loading && <Loading>Reading your progress</Loading>}
          {memory.loadError && (
            <Card role="alert">
              <p className="form-error">{memory.loadError}</p>
              <Button onClick={memory.reload}>Try again</Button>
            </Card>
          )}
          <ModuleList modules={modules} actions={actions} agentAvailable={me.agent.available} />
          <p className="muted">The admin has no practice space: nothing in the admin's job touches a discipline's work.</p>
        </div>
        <aside className="ra-onboarding__side" aria-label="Your guide">
          <AgentPanel conversationKey={key} mode="admin_onboarding" title="Your guide" emptyHint="The agent greets you here when you first sign in." />
        </aside>
      </div>
    </div>
  );
}

/** First login and onboarding (ui-direction screen 1). */
export function OnboardingPage() {
  const me = useCurrentMe();
  if (me.is_admin && !me.active) return <AdminOnboarding personId={me.person.id} />;
  if (!me.active) {
    return (
      <div className="ra-page stack">
        <Intro>A short, optional tour in small modules.</Intro>
        <EmptyState title="Choose a discipline first">
          The modules depend on your role in a discipline. Choose one at the top of the page, or ask the admin to add you to one.
        </EmptyState>
      </div>
    );
  }
  return <MemberOnboarding key={me.active.membership_id} membershipId={me.active.membership_id} />;
}
